import { useState } from "react";
import submit from "submit-form";

// Shared phase-template actions for an athlete - used by both the card-grid
// modal (phase-templates-modal.js) and the Annual Planner header's
// quick-apply dropdown (PhaseTemplateDropdown.js): applying a template (with
// confirmation first when that would wipe dated phases) and setting the
// coach's default template, both through the same set-athlete-phases route.
export const useApplyPhaseTemplate = ({
  athleteId,
  organizationCoachId,
  hasDatedPhases,
}) => {
  // The template awaiting confirmation, or null when nothing needs one.
  const [pendingTemplate, setPendingTemplate] = useState(null);

  const submitTemplate = (
    template,
    { saveDefaultTemplate = false, onlySetDefault = false } = {}
  ) => {
    let body = {
      phaseTemplate: template,
      athleteId,
      saveDefaultTemplate,
      onlySetDefault,
    };

    if (organizationCoachId) {
      body = { ...body, organizationCoachId };
    }

    submit("/actions/annual-planner/set-athlete-phases", {
      method: "POST",
      body,
    });
  };

  // The entry point every caller should use: applies immediately when the
  // athlete has no dated phases to lose, otherwise holds the template so
  // the caller can render a confirmation dialog before calling
  // confirmPendingTemplate.
  const requestApplyTemplate = (template, options) => {
    if (!hasDatedPhases) return submitTemplate(template, options);
    setPendingTemplate({ template, options });
  };

  const confirmPendingTemplate = () => {
    if (!pendingTemplate) return;
    submitTemplate(pendingTemplate.template, pendingTemplate.options);
    setPendingTemplate(null);
  };

  const cancelPendingTemplate = () => setPendingTemplate(null);

  // Never touches the athlete's dated phases - no confirmation needed,
  // one click.
  const setAsDefaultTemplate = (template) => {
    submitTemplate(template, {
      saveDefaultTemplate: true,
      onlySetDefault: true,
    });
  };

  return {
    pendingTemplate: pendingTemplate?.template ?? null,
    requestApplyTemplate,
    confirmPendingTemplate,
    cancelPendingTemplate,
    setAsDefaultTemplate,
  };
};
